// Role-based authorization helpers.
//
// Reads the actor's `app_role` from the active session. The 3-tier role model
// is: platform_admin > president > admin/staff > participant. NTRP visibility
// is a club setting that admins can override per-club; non-admins see NTRP
// only when ntrp_visibility === "all_members".
//
// These helpers are pure reads against `state` — no side effects, no API
// calls. Login/invite render flows (which DO have side effects) live in
// auth/screens.ts (extracted in a follow-up).

import { state } from "../state";

// Public-facing level categories (the ones non-admins can see). NTRP-derived
// "balance scores" are admin-only; the public level is a coarser bucket users
// pick when joining. Categories differ by gender per Korean tennis convention.
export const PUBLIC_LEVEL_CATEGORIES: Record<string, string[]> = {
  male: ["테생아", "테린이", "신인부", "오픈부"],
  female: ["테생아", "테린이", "개나리", "국화"],
};

export function levelOptionsForGender(gender: string | null | undefined): string[] {
  return (PUBLIC_LEVEL_CATEGORIES[gender as string] || []).slice();
}

export function isAdmin(): boolean {
  const role = state.session?.actor?.app_role;
  return role === "admin" || role === "staff" || role === "president" || role === "platform_admin";
}

export function isPresident(): boolean {
  const role = state.session?.actor?.app_role;
  return role === "president" || role === "platform_admin";
}

export function isPlatformAdmin(): boolean {
  return state.session?.actor?.app_role === "platform_admin";
}

export function canSeeNtrp(): boolean {
  if (isAdmin()) return true;
  return state.club?.ntrp_visibility === "all_members";
}

export function isMember(): boolean {
  return state.session?.actor?.app_role === "participant";
}

export function isDailyHost(ev: any): boolean {
  // Legacy: 이전 시스템에서 daily_host_member_id로 지정된 회원. 그랜드파더링용.
  if (!ev?.daily_host_member_id) return false;
  return ev.daily_host_member_id === state.session?.actor?.member_id;
}

export function isRegularEventCreator(ev: any): boolean {
  // 정기모임을 만든 사람 = 그 모임의 호스트. 특별전엔 적용 안 됨.
  if (!ev || ev.event_type !== "regular") return false;
  const myId = state.session?.actor?.member_id;
  return !!myId && ev.created_by === myId;
}

export function canManageEvent(ev: any): boolean {
  return isAdmin() || isRegularEventCreator(ev) || isDailyHost(ev);
}

export function adminRoleLabel(): string | null {
  const role = state.session?.actor?.app_role;
  if (role === "platform_admin") return "플랫폼 관리자";
  if (role === "president") return "회장";
  if (role === "admin") return "관리자";
  if (role === "staff") return "운영진";
  return null;
}

export function memberRoleLabel(role: string | null | undefined): string | null {
  if (role === "president") return "회장";
  if (role === "staff" || role === "admin") return "운영진";
  return null;
}

export function currentDisplayName(): string | null {
  return state.session?.actor?.name || null;
}
